import {
  Heading,
  Text,
  useColorModeValue,
  Tooltip,
  useDisclosure,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  VStack,
  Icon,
} from '@chakra-ui/react'
import { useToast } from '@/hooks/useToast'
import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiRefreshCw,
  FiRotateCcw,
  FiUsers,
  FiPlay,
  FiCopy,
  FiCheckCircle,
  FiEye,
} from 'react-icons/fi'
import {
  useContractRegistryRecords,
  useDeployContractRegistry,
} from '@/hooks/useAdminData'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { withNetworkStorageKey } from '@/utils/storageKeys'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'
import { ContractRegistryRecord, OrderListItem } from '@/types'
import { ContractPreparationTrigger } from '@/components/contractManagement/ContractPreparationTrigger'
import { ContractDeploymentTrigger } from '@/components/contractManagement/ContractDeploymentTrigger'
import { ContractVerificationTrigger } from '@/components/contractManagement/ContractVerificationTrigger'
import { ContractInitializationTrigger } from '@/components/contractManagement/ContractInitializationTrigger'
import { SettlementPreviewTrigger } from '@/components/contractManagement/SettlementPreviewTrigger'
import { formatDateTime, sanitizeTokenSymbol } from '@/utils/formatUtils'
import {
  extractAuditLogNote,
  extractNotesToPublic,
  normalizeNoteValue,
} from '@/utils/orderNotes'
import { normalizeOrderStatusCode } from '@/utils/statusUtils'
import { DeploymentTableTab } from './contract-registry/DeploymentTableTab'
import { StatusUpdateModal } from './contract-registry/StatusUpdateModal'
import { DeployDialog } from './contract-registry/DeployDialog'
import { RegistryDetailsModal } from './contract-registry/RegistryDetailsModal'
import { RegistryTableTab } from './contract-registry/RegistryTableTab'
import { orderService } from '@/services/admin/orderService'
import { auditService } from '@/services/admin/auditService'
import { contractService } from '@/services/admin/contractService'
import { useOrderPauseAvailability } from '@/hooks/useOrderPauseAvailability'

const CONTRACT_MANAGEMENT_TABS = [
  'contract_preparation',
  'registry_table',
  'contract_deployment',
  'deployment_table',
  'contract_verification',
  'contract_initialization',
  'settlement_preview',
] as const

export function ContractRegistryPage() {
  // All hooks must be called at the top level in a consistent order
  const { t } = useTranslation()
  const { toast } = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const cancelRef = useRef<HTMLButtonElement>(null)

  // State hooks - always called in the same order
  const [selectedRecord, setSelectedRecord] =
    useState<ContractRegistryRecord | null>(null)
  const [statusActionOrder, setStatusActionOrder] =
    useState<OrderListItem | null>(null)
  const [statusActionTarget, setStatusActionTarget] = useState<
    'compliance_hold' | 'closed'
  >('compliance_hold')
  const [statusActionAuditLog, setStatusActionAuditLog] = useState('')
  const [statusActionNotesToPublic, setStatusActionNotesToPublic] = useState('')
  const [statusActionCloseContract, setStatusActionCloseContract] =
    useState(false)
  const statusActionPauseAvailability = useOrderPauseAvailability()
  const statusActionChainCompatibility =
    statusActionOrder?.chainCompatibility ?? selectedRecord?.chain_compatibility
  const isStatusActionSolana =
    statusActionChainCompatibility?.toLowerCase() === 'solana'
  const isSolanaOrderPauseCapabilityLoading =
    isStatusActionSolana && !statusActionOrder
  const isSolanaOrderPauseUnsupported =
    isStatusActionSolana &&
    statusActionOrder !== null &&
    statusActionOrder?.solanaOrderPauseSupported !== true
  const isStatusActionPauseDisabled =
    isStatusActionSolana &&
    (isSolanaOrderPauseCapabilityLoading ||
      isSolanaOrderPauseUnsupported ||
      statusActionPauseAvailability.isLoading ||
      statusActionPauseAvailability.isUnavailable ||
      statusActionPauseAvailability.isPaused ||
      statusActionPauseAvailability.isClosed ||
      statusActionPauseAvailability.isProgramPaused ||
      statusActionPauseAvailability.hasPendingAction)
  const closeContractDisabledReasonKey =
    isSolanaOrderPauseCapabilityLoading ||
    statusActionPauseAvailability.isLoading
      ? 'orders.actions.pauseStateLoading'
      : isSolanaOrderPauseUnsupported
        ? 'orders.actions.solanaPauseUnsupported'
        : statusActionPauseAvailability.isUnavailable
          ? 'orders.actions.pauseStateUnavailable'
          : statusActionPauseAvailability.isPaused
            ? 'orders.actions.closeContractSkipReasons.alreadyPaused'
            : statusActionPauseAvailability.hasPendingAction
              ? 'orders.actions.pauseRequestPending'
              : statusActionPauseAvailability.isClosed
                ? 'orders.actions.pauseOrderClosed'
                : statusActionPauseAvailability.isProgramPaused
                  ? 'orders.actions.pauseProgramPaused'
                  : undefined
  const statusActionOrderIdRef = useRef<string | null>(null)
  const statusActionEnvironmentRef = useRef(currentEnv)
  const [isRefreshingDetail, setIsRefreshingDetail] = useState(false)
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)
  const [isDeploying, setIsDeploying] = useState(false)
  const TAB_STORAGE_KEY = useMemo(
    () =>
      withNetworkStorageKey('admin_portal_contract_registry_tab', currentEnv),
    [currentEnv]
  )
  const { activeTabIndex: tabIndex, handleTabChange } = useAdminTabQuery({
    tabs: CONTRACT_MANAGEMENT_TABS,
    defaultTab: 'contract_preparation',
    storageKey: TAB_STORAGE_KEY,
  })

  useEffect(() => {
    if (isStatusActionPauseDisabled) {
      setStatusActionCloseContract(false)
    }
  }, [isStatusActionPauseDisabled])

  // Modal hooks - always called in the same order
  const statusModal = useDisclosure()
  const deployModal = useDisclosure()
  const detailModal = useDisclosure()

  useEffect(() => {
    if (statusActionEnvironmentRef.current === currentEnv) {
      return
    }
    statusActionEnvironmentRef.current = currentEnv
    setSelectedRecord(null)
    setStatusActionOrder(null)
    setStatusActionCloseContract(false)
    statusActionOrderIdRef.current = null
    statusActionPauseAvailability.reset()
    statusModal.onClose()
  }, [currentEnv, statusActionPauseAvailability.reset, statusModal.onClose])

  // Pagination state
  const limit = 20

  const {
    data: allData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useContractRegistryRecords(undefined, {
    enabled: tabIndex === 1,
    fetchAll: true,
  })

  const deployMutation = useDeployContractRegistry()

  // Color mode hooks - all hooks must be called at top level
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const modalBg = useColorModeValue('gray.50', 'gray.800')

  // Memoized utility functions to prevent re-renders
  const getFormattedAmount = useCallback((record: ContractRegistryRecord) => {
    let symbol = sanitizeTokenSymbol(record.token_symbol)
    let amountText = ''

    if (
      typeof record.formatted_amount_value === 'number' &&
      Number.isFinite(record.formatted_amount_value)
    ) {
      const fractionDigits = record.formatted_amount_value < 1 ? 6 : 2
      amountText = record.formatted_amount_value.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })
    } else if (record.formatted_amount) {
      const normalized = record.formatted_amount.trim()
      const upperSymbol = symbol.toUpperCase()
      if (upperSymbol && normalized.toUpperCase().endsWith(upperSymbol)) {
        amountText = normalized
          .slice(0, normalized.length - symbol.length)
          .trim()
      } else {
        const parts = normalized.split(/\s+/u)
        if (parts.length > 1) {
          const possibleSymbol = sanitizeTokenSymbol(parts[parts.length - 1])
          if (possibleSymbol && possibleSymbol !== 'UNKNOWN') {
            symbol = possibleSymbol
          }
          amountText = parts.slice(0, -1).join(' ')
        } else {
          amountText = normalized
        }
      }
    } else if (record.amount) {
      amountText = record.amount
    } else if (record.amount_in_smallest_unit) {
      amountText = record.amount_in_smallest_unit
    } else {
      amountText = '0'
    }

    return { amountText, symbol }
  }, [])

  const getStatusDisplayName = useCallback(
    (record: ContractRegistryRecord) => {
      const statusKey =
        record.status_name ||
        record.status_id?.replace('orrs_', '') ||
        record.status?.replace('orrs_', '')
      return t(`contractRegistry.status.${statusKey}`)
    },
    [t]
  )

  // Parse user_payments JSON and get count
  const getUserPaymentsInfo = useCallback((record: ContractRegistryRecord) => {
    const explicitCount =
      typeof record.user_payments_count === 'number'
        ? record.user_payments_count
        : Number.isFinite(Number(record.user_payments_count))
          ? Number(record.user_payments_count)
          : undefined

    if (explicitCount !== undefined) {
      return { count: explicitCount, isEmpty: explicitCount === 0 }
    }

    if (
      typeof record.user_payments !== 'string' ||
      record.user_payments.trim().length === 0
    ) {
      return { count: 0, isEmpty: true }
    }

    try {
      const payments = JSON.parse(record.user_payments)
      if (Array.isArray(payments)) {
        return { count: payments.length, isEmpty: payments.length === 0 }
      } else if (typeof payments === 'object' && payments !== null) {
        // If it's an object, count the keys
        const count = Object.keys(payments).length
        return { count, isEmpty: count === 0 }
      }
      return { count: 0, isEmpty: true }
    } catch (error) {
      console.warn('Failed to parse user_payments JSON:', error)
      return { count: 0, isEmpty: true }
    }
  }, [])

  // Format last sync time
  const formatLastSyncTime = useCallback((timestamp?: string) => {
    if (!timestamp) return '-'
    return formatDateTime(timestamp, { fallback: '-' })
  }, [])

  const resolveRegistryChainId = useCallback(
    (record?: ContractRegistryRecord | null) => {
      if (!record) return ''
      if (record.chain_id) return record.chain_id
      if (record.chain_code) return record.chain_code
      const registryChain = (record as any).registry_chain_id
      if (
        typeof registryChain === 'string' &&
        registryChain.trim().length > 0
      ) {
        return registryChain
      }
      const numeric = (record as any).chain_numeric_id
      if (typeof numeric === 'number' && Number.isFinite(numeric)) {
        return `${numeric}`
      }
      return ''
    },
    []
  )

  const setNotesToPublicIfEmpty = (value?: string | null) => {
    const normalized = normalizeNoteValue(value)
    if (!normalized) {
      return
    }
    setStatusActionNotesToPublic(current =>
      current.trim() ? current : normalized
    )
  }

  const resolveOrderIdentifier = (record: ContractRegistryRecord): string => {
    const candidate = record.orderId ?? record.order_id ?? record.id ?? ''
    return typeof candidate === 'string' ? candidate : ''
  }

  const loadStatusActionAuditLog = async (orderIdentifier: string) => {
    if (!orderIdentifier) {
      return
    }

    const targetOrderId = orderIdentifier
    try {
      const response = await auditService.getAuditLogs({
        page: 1,
        limit: 10,
        resource_type: 'order',
        resource_id: orderIdentifier,
      })

      if (!response.success) {
        return
      }

      const logs = Array.isArray(response.data) ? response.data : []
      const targetLog = logs.find(log => extractAuditLogNote(log.details))
      if (targetLog) {
        const auditLog = extractAuditLogNote(targetLog.details)
        if (auditLog && statusActionOrderIdRef.current === targetOrderId) {
          setStatusActionAuditLog(current =>
            current.trim() ? current : auditLog
          )
        }
      }

      const noteLog = logs.find(log => extractNotesToPublic(log.details))
      if (noteLog) {
        const notes = normalizeNoteValue(extractNotesToPublic(noteLog.details))
        if (notes && statusActionOrderIdRef.current === targetOrderId) {
          setNotesToPublicIfEmpty(notes)
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ContractRegistryPage] Failed to load audit log', error)
      }
    }
  }

  const loadStatusActionOrderDetails = async (orderIdentifier: string) => {
    if (!orderIdentifier) {
      return
    }

    try {
      const response = await orderService.getAllOrders(
        {
          page: 1,
          pageSize: 1,
          search: orderIdentifier,
        },
        currentEnv
      )

      const matches = Array.isArray(response.data) ? response.data : []
      const matched =
        matches.find(item => item.orderId === orderIdentifier) ?? matches[0]
      if (!matched || statusActionOrderIdRef.current !== orderIdentifier) {
        return
      }

      setStatusActionOrder(matched)

      const normalizedNotes = normalizeNoteValue(matched.notesToPublic ?? null)
      if (normalizedNotes) {
        setNotesToPublicIfEmpty(normalizedNotes)
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[ContractRegistryPage] Failed to refresh order details',
          error
        )
      }
    }
  }

  const handleStatusUpdate = useCallback(
    (record: ContractRegistryRecord) => {
      const orderIdentifier = resolveOrderIdentifier(record)
      setSelectedRecord(record)
      setStatusActionOrder(null)
      setStatusActionTarget('compliance_hold')
      setStatusActionAuditLog('')
      setStatusActionNotesToPublic('')
      setStatusActionCloseContract(false)
      statusActionPauseAvailability.reset()
      statusActionOrderIdRef.current = orderIdentifier
      statusModal.onOpen()
      void loadStatusActionAuditLog(orderIdentifier)
      void loadStatusActionOrderDetails(orderIdentifier)
      if (record.chain_compatibility?.toLowerCase() === 'solana') {
        void statusActionPauseAvailability.load(orderIdentifier, currentEnv)
      }
    },
    [
      loadStatusActionAuditLog,
      loadStatusActionOrderDetails,
      currentEnv,
      resolveOrderIdentifier,
      statusActionPauseAvailability.load,
      statusActionPauseAvailability.reset,
      statusModal,
    ]
  )

  const handleCloseStatusModal = () => {
    setSelectedRecord(null)
    setStatusActionOrder(null)
    setStatusActionTarget('compliance_hold')
    setStatusActionAuditLog('')
    setStatusActionNotesToPublic('')
    setStatusActionCloseContract(false)
    statusActionPauseAvailability.reset()
    statusActionOrderIdRef.current = null
    statusModal.onClose()
  }

  const handleDeploy = useCallback(
    (record: ContractRegistryRecord) => {
      setSelectedRecord(record)
      setIsDeploying(false) // Reset deployment state
      deployModal.onOpen()
    },
    [deployModal]
  )

  const handleViewDetails = useCallback(
    (record: ContractRegistryRecord) => {
      setSelectedRecord(record)
      detailModal.onOpen()

      void (async () => {
        try {
          const response = await contractService.getContractRegistryRecord(
            record.id,
            currentEnv
          )
          if (response?.success && response.data) {
            setSelectedRecord(response.data)
          }
        } catch (error) {
          toast({
            title: t('common.error'),
            description:
              error instanceof Error ? error.message : t('common.unknownError'),
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
        }
      })()
    },
    [currentEnv, detailModal, t, toast]
  )

  const handleRefreshDetails = useCallback(async () => {
    if (!selectedRecord?.id) {
      return
    }

    setIsRefreshingDetail(true)
    try {
      const response = await contractService.getContractRegistryRecord(
        selectedRecord.id,
        currentEnv
      )
      if (response?.success && response.data) {
        setSelectedRecord(response.data)
      }
      await refetch()
    } catch (error) {
      toast({
        title: t('common.error'),
        description:
          error instanceof Error ? error.message : t('common.unknownError'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setIsRefreshingDetail(false)
    }
  }, [currentEnv, refetch, selectedRecord?.id, t, toast])

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text)
      toast({
        title: t('common.success'),
        description: t('contractRegistry.actions.addressCopied'),
        status: 'success',
        duration: 2000,
      })
    },
    [t, toast]
  )

  const closeContractSkipReasonKeyMap: Record<string, string> = {
    queue_unavailable:
      'orders.actions.closeContractSkipReasons.queueUnavailable',
    deployment_missing:
      'orders.actions.closeContractSkipReasons.deploymentMissing',
    deployment_not_successful:
      'orders.actions.closeContractSkipReasons.deploymentNotSuccessful',
    address_not_verified:
      'orders.actions.closeContractSkipReasons.addressNotVerified',
    contract_address_missing:
      'orders.actions.closeContractSkipReasons.contractAddressMissing',
    chain_missing: 'orders.actions.closeContractSkipReasons.chainMissing',
    chain_descriptor_unavailable:
      'orders.actions.closeContractSkipReasons.chainDescriptorUnavailable',
    refundable_balance_nonzero:
      'orders.actions.closeContractSkipReasons.refundableBalanceNonzero',
    refundable_balance_check_failed:
      'orders.actions.closeContractSkipReasons.refundableBalanceCheckFailed',
    queue_enqueue_failed:
      'orders.actions.closeContractSkipReasons.queueEnqueueFailed',
    already_paused: 'orders.actions.closeContractSkipReasons.alreadyPaused',
    active_registry_remaining:
      'orders.actions.closeContractSkipReasons.activeRegistryRemaining',
    hold_commit_read_unavailable:
      'orders.actions.closeContractSkipReasons.holdCommitReadUnavailable',
    status_update_outcome_uncertain:
      'orders.actions.closeContractSkipReasons.statusUpdateOutcomeUncertain',
    solana_governance_enqueue_pending:
      'orders.actions.closeContractSkipReasons.governanceEnqueuePending',
    solana_governance_terminal:
      'orders.actions.closeContractSkipReasons.governanceTerminal',
    opposite_governance_request_active:
      'orders.actions.closeContractSkipReasons.oppositeGovernanceActive',
  }

  const getOrderStatusText = (statusCode: string | undefined) => {
    if (!statusCode) return t('common.pending')
    const normalized = normalizeOrderStatusCode(statusCode)
    const statusMap: Record<string, string> = {
      awaiting_payment: 'orders.status.awaitingPayment',
      underpaid: 'orders.status.underpaid',
      fullpaid: 'orders.status.fullPaid',
      overpaid: 'orders.status.overpaid',
      expired: 'orders.status.expired',
      settled: 'orders.status.settled',
      refunded: 'orders.status.refunded',
      compliance_hold: 'orders.status.complianceHold',
      closed: 'orders.status.closed',
    }
    const key = statusMap[normalized as keyof typeof statusMap]
    if (key) {
      return t(key)
    }
    return normalized.replace(/_/g, ' ').toUpperCase()
  }

  const confirmStatusUpdate = async () => {
    if (!selectedRecord) return

    const orderIdentifier = resolveOrderIdentifier(selectedRecord)
    if (!orderIdentifier) {
      toast({
        title: t('orders.actions.statusUpdateFailed'),
        description: t('common.unknownError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    const auditLog = statusActionAuditLog.trim()
    if (!auditLog) {
      toast({
        title: t('orders.actions.auditLogRequired'),
        description: t('orders.actions.auditLogRequiredDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    setIsStatusUpdating(true)
    try {
      const response = await orderService.onholdOrCloseOrder(
        orderIdentifier,
        {
          status: statusActionTarget,
          auditLog,
          notesToPublic: statusActionNotesToPublic.trim() || undefined,
          closeContract:
            statusActionCloseContract && !isStatusActionPauseDisabled,
        },
        currentEnv
      )

      if (!response.success) {
        const message =
          response.error ??
          response.message ??
          t('orders.actions.statusUpdateFailed')
        throw new Error(message)
      }

      if (response.data?.closeContractSkipReason && statusActionCloseContract) {
        const skipReasonKey =
          closeContractSkipReasonKeyMap[
            response.data.closeContractSkipReason
          ] ?? 'orders.actions.closeContractSkippedDescription'
        toast({
          title: t('orders.actions.closeContractSkipped'),
          description: t(skipReasonKey),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        })
      } else if (
        response.data?.closeContractQueued &&
        statusActionCloseContract
      ) {
        toast({
          title: t('orders.actions.pauseQueued'),
          description: t('orders.actions.pauseQueuedDescription'),
          status: 'info',
          duration: 5000,
          isClosable: true,
        })
      }

      toast({
        title: t('orders.actions.statusUpdated'),
        description: t('orders.actions.statusUpdatedDescription'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      handleCloseStatusModal()
      refetch()
    } catch (error) {
      toast({
        title: t('orders.actions.statusUpdateFailed'),
        description:
          error instanceof Error ? error.message : t('common.unknownError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsStatusUpdating(false)
    }
  }

  const confirmDeploy = async () => {
    if (!selectedRecord) return

    setIsDeploying(true)
    try {
      const result = await deployMutation.mutateAsync({
        id: selectedRecord.id,
      })
      toast({
        title: t('contractRegistry.success.deploymentNormal'),
        description:
          result.data?.message ||
          t('contractRegistry.success.deploymentNormalDesc'),
        status: 'success',
        duration: 6000,
        isClosable: true,
      })

      deployModal.onClose()

      // Refresh data after successful deployment to show updated status
      refetch()
    } catch (error) {
      let errorTitle = t('contractRegistry.error.deploymentFailed')
      let errorMessage = t('common.unknownError')

      // Handle different types of errors
      if (error instanceof Error) {
        errorMessage = error.message
      } else if ((error as any)?.response?.data) {
        const errorData = (error as any).response.data
        errorMessage =
          errorData.error || errorData.message || t('common.unknownError')

        // Handle specific error types
        if (errorData.code === 'RATE_LIMIT_EXCEEDED') {
          errorTitle = t('contractRegistry.error.rateLimitExceeded')
          errorMessage = t('contractRegistry.error.rateLimitMessage')
        } else if (errorData.code === 'INSUFFICIENT_GAS') {
          errorTitle = t('contractRegistry.error.insufficientGas')
          errorMessage = t('contractRegistry.error.insufficientGasMessage')
        } else if (errorData.code === 'INVALID_STATUS') {
          errorTitle = t('contractRegistry.error.invalidStatus')
          errorMessage = t('contractRegistry.error.invalidStatusMessage')
        }

        // Handle "Atomic update failed" errors more gracefully
        if (errorMessage.includes('Atomic update failed')) {
          // Don't show this technical error to users, just show generic message
          errorMessage = t('contractRegistry.error.deploymentFailed')
        }
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        status: 'error',
        duration: 8000,
        isClosable: true,
      })

      // Keep modal open on error for user to retry or cancel
    } finally {
      setIsDeploying(false)
    }
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <Flex justify="space-between" align="flex-start">
          <Box>
            <Heading size="lg" mb={2}>
              Contract Management
            </Heading>
            <Text color={textColor}>{t('contractRegistry.description')}</Text>
          </Box>
        </Flex>

        {/* Contract Management Tabs */}
        <Tabs
          variant="enclosed"
          colorScheme="admin"
          isLazy
          lazyBehavior="keepMounted"
          index={tabIndex}
          onChange={handleTabChange}
        >
          <TabList
            justifyContent={{ base: 'flex-start', md: 'space-between' }}
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            <Tooltip
              label={t('contractRegistry.tabs.contractPreparation')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('contractRegistry.tabs.contractPreparation')}
              >
                <Icon as={FiUsers} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('contractRegistry.tabs.registryTable')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('contractRegistry.tabs.registryTable')}
              >
                <Icon as={FiRefreshCw} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('contractRegistry.tabs.contractDeployment')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('contractRegistry.tabs.contractDeployment')}
              >
                <Icon as={FiPlay} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('contractRegistry.tabs.deploymentTable')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('contractRegistry.tabs.deploymentTable')}
              >
                <Icon as={FiCopy} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('contractRegistry.tabs.contractVerification')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('contractRegistry.tabs.contractVerification')}
              >
                <Icon as={FiCheckCircle} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('contractRegistry.tabs.contractInitialization')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('contractRegistry.tabs.contractInitialization')}
              >
                <Icon as={FiRotateCcw} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('contractRegistry.tabs.settlementPreview')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('contractRegistry.tabs.settlementPreview')}
              >
                <Icon as={FiEye} boxSize={6} />
              </Tab>
            </Tooltip>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              {/* Contract Preparation Tab */}
              <Box>
                <Text fontSize="sm" color={textColor} mb={4}>
                  {t('contractRegistry.tabs.preparationDescription')}
                </Text>
                <ContractPreparationTrigger />
              </Box>
            </TabPanel>

            <TabPanel p={0} display="flex" flexDirection="column">
              <RegistryTableTab
                data={allData?.data}
                isLoading={isLoading}
                error={error}
                isFetching={isFetching}
                onRefresh={refetch}
                onSelectStatusUpdate={handleStatusUpdate}
                onSelectDeploy={handleDeploy}
                onSelectDetails={handleViewDetails}
                isDeploying={isDeploying}
                deployingRecordId={selectedRecord?.id ?? null}
                getStatusDisplayName={getStatusDisplayName}
                getFormattedAmount={getFormattedAmount}
                getUserPaymentsInfo={getUserPaymentsInfo}
                formatLastSyncTime={formatLastSyncTime}
                copyToClipboard={copyToClipboard}
                resolveRegistryChainId={resolveRegistryChainId}
              />

              <StatusUpdateModal
                isOpen={statusModal.isOpen}
                onClose={handleCloseStatusModal}
                currentStatusLabel={
                  statusActionOrder
                    ? getOrderStatusText(statusActionOrder.status)
                    : t('common.pending')
                }
                targetStatus={statusActionTarget}
                onTargetStatusChange={setStatusActionTarget}
                auditLog={statusActionAuditLog}
                notesToPublic={statusActionNotesToPublic}
                closeContract={statusActionCloseContract}
                isCloseContractDisabled={isStatusActionPauseDisabled}
                closeContractDisabledReasonKey={closeContractDisabledReasonKey}
                onChangeAuditLog={setStatusActionAuditLog}
                onChangeNotesToPublic={setStatusActionNotesToPublic}
                onChangeCloseContract={setStatusActionCloseContract}
                onConfirm={confirmStatusUpdate}
                isSubmitting={isStatusUpdating}
                t={t}
                textColor={textColor}
              />

              <DeployDialog
                isOpen={deployModal.isOpen}
                onClose={deployModal.onClose}
                selectedRecord={selectedRecord}
                isDeploying={isDeploying}
                confirmDeploy={confirmDeploy}
                textColor={textColor}
                modalBg={modalBg}
                borderColor={borderColor}
                getFormattedAmount={getFormattedAmount}
                resolveRegistryChainId={resolveRegistryChainId}
                t={t}
                cancelRef={cancelRef}
              />

              <RegistryDetailsModal
                isOpen={detailModal.isOpen}
                onClose={detailModal.onClose}
                selectedRecord={selectedRecord}
                isRefreshing={isRefreshingDetail}
                onRefresh={handleRefreshDetails}
                getStatusDisplayName={getStatusDisplayName}
                resolveRegistryChainId={resolveRegistryChainId}
                getFormattedAmount={getFormattedAmount}
                copyToClipboard={copyToClipboard}
                textColor={textColor}
                t={t}
              />
            </TabPanel>

            <TabPanel px={0}>
              {/* Contract Deployment Tab */}
              <Box>
                <Text fontSize="sm" color={textColor} mb={4}>
                  {t('contractRegistry.tabs.deploymentDescription')}
                </Text>
                <ContractDeploymentTrigger />
              </Box>
            </TabPanel>

            <TabPanel p={0} display="flex" flexDirection="column">
              <DeploymentTableTab pageSize={limit} />
            </TabPanel>

            <TabPanel px={0}>
              {/* Contract Verification Tab */}
              <Box>
                <Text fontSize="sm" color={textColor} mb={4}>
                  {t('contractRegistry.tabs.verificationDescription')}
                </Text>
                <ContractVerificationTrigger />
              </Box>
            </TabPanel>

            <TabPanel px={0}>
              {/* Contract Initialization Tab */}
              <Box>
                <Text fontSize="sm" color={textColor} mb={4}>
                  {t('contractRegistry.tabs.initializationDescription')}
                </Text>
                <ContractInitializationTrigger />
              </Box>
            </TabPanel>

            <TabPanel px={0}>
              {/* Settlement Preview Tab */}
              <Box>
                <Text fontSize="sm" color={textColor} mb={4}>
                  {t('contractRegistry.tabs.settlementPreviewDescription')}
                </Text>
                <SettlementPreviewTrigger />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  )
}
